// src/features/admin/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AuthState {
  token: string | null;
  email: string | null;
  userId: string | null;
  userName: string | null;
  role: string | null; // Changed from 'admin' | 'vendor' | null to string
  adminType: string | null;
  isAuthenticated: boolean;
  vendorId: string | null;
}

const initialState: AuthState = {
  token: null,
  email: null,
  userId: null,
  userName: null,
  role: null,
  adminType: null,
  isAuthenticated: false,
  vendorId: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAdminCredentials: (state, action: PayloadAction<{
      token: string;
      email: string;
      userId: string;
      userName: string;
      role?: string;
      adminType?: string;
      data?: any;
    }>) => {
      // CRITICAL FIX: Use the provided role or adminType
      // If adminType is provided, use it. Otherwise use role. Default to 'admin'
      const adminType = action.payload.adminType || action.payload.role || 'admin';
      
      // The actual role should match what we get from backend
      const role = action.payload.role || adminType || 'admin';
      
      console.log('🔐 setAdminCredentials called with:', {
        providedRole: action.payload.role,
        providedAdminType: action.payload.adminType,
        finalAdminType: adminType,
        finalRole: role,
        email: action.payload.email
      });
      
      state.token = action.payload.token;
      state.email = action.payload.email;
      state.userId = action.payload.userId;
      state.userName = action.payload.userName;
      state.role = role; // Set the actual role from backend
      state.adminType = adminType; // Set adminType
      state.isAuthenticated = true;
      state.vendorId = null;
      
      // Store in localStorage - CRITICAL: Make sure admin_type is set correctly
      localStorage.setItem('admin_token', action.payload.token);
      localStorage.setItem('user_email', action.payload.email);
      localStorage.setItem('user_id', action.payload.userId);
      localStorage.setItem('user_name', action.payload.userName);
      localStorage.setItem('user_role', role); // Store the actual role
      localStorage.setItem('admin_type', adminType); // Store admin type
      
      // Store additional data if available
      if (action.payload.data) {
        localStorage.setItem('admin_data', JSON.stringify(action.payload.data));
      }
      
      localStorage.removeItem('vendor_token');
      localStorage.removeItem('vendor_id');
      
      // Debug: Verify what was stored
      console.log('✅ Stored in localStorage:', {
        admin_type: localStorage.getItem('admin_type'),
        user_role: localStorage.getItem('user_role'),
        token: !!localStorage.getItem('admin_token')
      });
    },
    setVendorCredentials: (state, action: PayloadAction<{
      token: string;
      email: string;
      vendorId?: string | number;
      userName: string;
      role?: string;
    }>) => {
      // Convert vendorId to string and ensure it exists
      const vendorIdStr = action.payload.vendorId?.toString() || '';
      
      state.token = action.payload.token;
      state.email = action.payload.email;
      state.userId = vendorIdStr;
      state.userName = action.payload.userName;
      state.role = 'vendor'; // Always 'vendor' for vendor login
      state.adminType = null; // Not applicable for vendors
      state.isAuthenticated = true;
      state.vendorId = vendorIdStr;
      
      console.log('Redux: Storing vendor credentials', {
        vendorId: vendorIdStr,
        userName: action.payload.userName,
        email: action.payload.email
      });
      
      // Store in localStorage
      localStorage.setItem('vendor_token', action.payload.token);
      localStorage.setItem('user_email', action.payload.email);
      localStorage.setItem('user_id', vendorIdStr);
      localStorage.setItem('user_name', action.payload.userName);
      localStorage.setItem('user_role', 'vendor');
      localStorage.setItem('vendor_id', vendorIdStr);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_type');
      localStorage.removeItem('admin_data');
    },
    clearCredentials: (state) => {
      state.token = null;
      state.email = null;
      state.userId = null;
      state.userName = null;
      state.role = null;
      state.adminType = null;
      state.isAuthenticated = false;
      state.vendorId = null;
      
      // Clear localStorage
      localStorage.removeItem('admin_token');
      localStorage.removeItem('vendor_token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_name');
      localStorage.removeItem('vendor_id');
      localStorage.removeItem('admin_type');
      localStorage.removeItem('admin_data');
    },
    initializeAuth: (state) => {
      // Check for vendor token first, then admin token
      const vendorToken = localStorage.getItem('vendor_token');
      const adminToken = localStorage.getItem('admin_token');
      const token = vendorToken || adminToken;
      
      const role = localStorage.getItem('user_role');
      const email = localStorage.getItem('user_email');
      const userId = localStorage.getItem('user_id');
      const userName = localStorage.getItem('user_name');
      const vendorId = localStorage.getItem('vendor_id');
      const adminType = localStorage.getItem('admin_type');

      console.log('🔍 Initializing auth from localStorage:', {
        role,
        vendorId,
        userName,
        adminType,
        hasToken: !!token,
        tokenType: vendorToken ? 'vendor' : adminToken ? 'admin' : 'none'
      });

      if (token && role && email) {
        state.token = token;
        state.email = email;
        state.userId = userId;
        state.userName = userName;
        state.role = role;
        state.adminType = adminType;
        state.isAuthenticated = true;
        state.vendorId = role === 'vendor' ? vendorId : null;
        
        console.log('✅ Auth initialized:', {
          role: state.role,
          adminType: state.adminType,
          isAuthenticated: state.isAuthenticated
        });
      } else {
        console.log('❌ Auth not initialized - missing required data');
      }
    },
    setAdminType: (state, action: PayloadAction<string>) => {
      console.log('🔄 setAdminType called with:', action.payload);
      
      state.adminType = action.payload;
      state.role = action.payload; // Also update the role
      
      localStorage.setItem('admin_type', action.payload);
      localStorage.setItem('user_role', action.payload);
      
      console.log('✅ Admin type updated:', {
        adminType: state.adminType,
        role: state.role
      });
    },
    
    // New action to force update role (for debugging/testing)
    forceSetRole: (state, action: PayloadAction<{role: string; adminType: string}>) => {
      console.log('🛠️ forceSetRole called with:', action.payload);
      
      state.role = action.payload.role;
      state.adminType = action.payload.adminType;
      
      localStorage.setItem('user_role', action.payload.role);
      localStorage.setItem('admin_type', action.payload.adminType);
      
      console.log('✅ Role forced to:', {
        role: state.role,
        adminType: state.adminType
      });
    },
  },
}); 

export const { 
  setAdminCredentials, 
  setVendorCredentials, 
  clearCredentials, 
  initializeAuth,
  setAdminType,
  forceSetRole // Export the new action
} = authSlice.actions;

export default authSlice.reducer;